import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { courseAPI, chatAPI } from '@/services/api';
import { STORAGE_KEYS } from '@/constants';

const CourseContext = createContext();

const initialState = {
  course: null,
  currentStep: 1,
  chatHistory: [],
  isChatOpen: false,
  loading: false,
  error: null,
  // Track which steps have already sent their welcome message to avoid duplicates
  welcomesSent: {},
  // Import mode state
  importMode: false,
  importSessionId: null,
  executionPlan: null
};

function courseReducer(state, action) {
  switch (action.type) {
    case 'SET_COURSE':
      return { ...state, course: action.payload };
    case 'SET_STEP':
      return { ...state, currentStep: action.payload };
    case 'UPDATE_COURSE':
      return { ...state, course: { ...state.course, ...action.payload } };
    case 'SET_CHAT_HISTORY':
      return { ...state, chatHistory: action.payload };
    case 'ADD_CHAT_MESSAGE':
      return { ...state, chatHistory: [...state.chatHistory, action.payload] };
    case 'REMOVE_LAST_MESSAGE':
      return { ...state, chatHistory: state.chatHistory.slice(0, -1) };
    case 'REMOVE_LOADING_MESSAGES':
      return { ...state, chatHistory: state.chatHistory.filter(msg => !msg.isLoading) };
    case 'TOGGLE_CHAT':
      return { ...state, isChatOpen: !state.isChatOpen };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'MARK_WELCOME_SENT': {
      const step = action.payload;
      return { ...state, welcomesSent: { ...state.welcomesSent, [step]: true } };
    }
    case 'MARK_WELCOME_NOT_SENT': {
      const step = action.payload;
      const newWelcomesSent = { ...state.welcomesSent };
      delete newWelcomesSent[step];
      return { ...state, welcomesSent: newWelcomesSent };
    }
    case 'SET_IMPORT_MODE':
      return {
        ...state,
        importMode: true,
        importSessionId: action.payload.sessionId,
        executionPlan: action.payload.executionPlan
      };
    case 'UPDATE_EXECUTION_PLAN':
      return {
        ...state,
        executionPlan: { ...state.executionPlan, ...action.payload }
      };
    case 'CLEAR_IMPORT_MODE':
      return {
        ...state,
        importMode: false,
        importSessionId: null,
        executionPlan: null
      };
    default:
      return state;
  }
}

export function CourseProvider({ children }) {
  const [state, dispatch] = useReducer(courseReducer, initialState);

  // Initialize from storage or URL params only ONCE
  useEffect(() => {
    const initCourse = async () => {
      // Check if we're in import mode from URL params
      const urlParams = new URLSearchParams(window.location.search);
      const importSessionId = urlParams.get('importSessionId');
      const courseId = urlParams.get('courseId');

      if (importSessionId && courseId) {
        // Import mode logic
        try {
          const course = await courseAPI.getById(courseId);
          dispatch({ type: 'SET_COURSE', payload: course });
          dispatch({
            type: 'SET_IMPORT_MODE',
            payload: {
              sessionId: importSessionId,
              executionPlan: null
            }
          });
          localStorage.setItem(STORAGE_KEYS.IMPORT_COURSE_ID, courseId);
          localStorage.setItem(STORAGE_KEYS.IMPORT_SESSION_ID, importSessionId);
          localStorage.removeItem(STORAGE_KEYS.COURSE_ID);
          return;
        } catch (error) {
          console.error('Error loading import course:', error);
        }
      }

      // Normal mode: Load existing course from localStorage if available
      const storedCourseId = localStorage.getItem(STORAGE_KEYS.COURSE_ID);
      if (storedCourseId) {
        try {
          const course = await courseAPI.getById(storedCourseId);
          dispatch({ type: 'SET_COURSE', payload: course });
        } catch (error) {
          // If stored course is invalid, clear it but DO NOT auto-create a new one
          // User should be sent to LandingChoice
          localStorage.removeItem(STORAGE_KEYS.COURSE_ID);
        }
      }
    };

    initCourse();
  }, []);

  // Ensure a course exists (used by actions that need course immediately)
  const ensureCourse = async () => {
    if (state.course) return state.course;
    // Try using any stored course ID first
    const storedCourseId = localStorage.getItem(STORAGE_KEYS.COURSE_ID);
    if (storedCourseId) {
      try {
        const existing = await courseAPI.getById(storedCourseId);
        dispatch({ type: 'SET_COURSE', payload: existing });
        return existing;
      } catch (_) {
        // fall through to create
      }
    }
    try {
      const course = await courseAPI.create();
      dispatch({ type: 'SET_COURSE', payload: course });
      localStorage.setItem(STORAGE_KEYS.COURSE_ID, course.id);
      return course;
    } catch (error) {
      console.error('ensureCourse error:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to initialize course' });
      return null;
    }
  };

  const updateCourse = async (updates) => {
    if (!state.course) return;
    try {
      const course = await courseAPI.update(state.course.id, updates);
      dispatch({ type: 'SET_COURSE', payload: course });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to update course' });
    }
  };

  const createCheckpoint = async (label, stepIndex) => {
    if (!state.course) return;
    try {
      await courseAPI.createCheckpoint(state.course.id, label, stepIndex, state.chatHistory);
    } catch (error) {
      console.error('Error creating checkpoint:', error);
    }
  };

  const restoreCheckpoint = async (versionId) => {
    if (!state.course) return;
    try {
      const data = await courseAPI.restoreCheckpoint(state.course.id, versionId);
      const course = await courseAPI.getById(state.course.id);
      dispatch({ type: 'SET_COURSE', payload: course });
      dispatch({ type: 'SET_CHAT_HISTORY', payload: data.chatHistory || [] });
    } catch (error) {
      console.error('Error restoring checkpoint:', error);
    }
  };

  const sendChatMessage = async (message) => {
    // Always show the user's message immediately for better UX
    dispatch({ type: 'ADD_CHAT_MESSAGE', payload: { role: 'user', text: message } });

    // Show loading indicator
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'ADD_CHAT_MESSAGE', payload: { role: 'assistant', text: '...', isLoading: true } });

    // Ensure we have a course; create one on-demand if missing
    let course = state.course;
    if (!course) {
      course = await ensureCourse();
      if (!course) {
        dispatch({ type: 'SET_LOADING', payload: false });
        // Remove loading message and add error
        dispatch({ type: 'REMOVE_LAST_MESSAGE' });
        dispatch({ type: 'ADD_CHAT_MESSAGE', payload: { role: 'assistant', text: 'Unable to initialize course. Please try again.' } });
        return;
      }
    }

    try {
      let data;

      // Check if we're in import mode - use import execution endpoint
      if (state.importMode && state.importSessionId) {
        // Import mode: use import execution endpoint
        const { sendExecutionMessage } = await import('@/services/importApi');
        data = await sendExecutionMessage(state.importSessionId, message);
      } else {
        // Regular mode: use standard chat endpoint
        data = await chatAPI.sendMessage(course.id, message, state.currentStep);
      }

      // Remove loading message
      dispatch({ type: 'REMOVE_LAST_MESSAGE' });

      // Only add assistant message if it's not hidden
      if (!data.hide_message && data.response) {
        dispatch({ type: 'ADD_CHAT_MESSAGE', payload: { role: 'assistant', text: data.response } });
      }

      // Update course data if returned from the chat response
      if (data.course_data) {
        dispatch({ type: 'SET_COURSE', payload: data.course_data });
      }

      // Handle step transition if indicated by the backend
      if (data.step_complete && data.next_step) {
        // CRITICAL: Clear chat history when transitioning to new step
        dispatch({ type: 'SET_CHAT_HISTORY', payload: [] });

        // Transition to next step
        dispatch({ type: 'SET_STEP', payload: data.next_step });

        // Mark welcome as not sent for the new step so we get a fresh welcome
        dispatch({ type: 'MARK_WELCOME_NOT_SENT', payload: data.next_step });

        console.log(`✅ Step transition: ${state.currentStep} → ${data.next_step}`);
      }
    } catch (error) {
      console.error('sendChatMessage error:', error);
      // Remove loading message and add error
      dispatch({ type: 'REMOVE_LAST_MESSAGE' });
      dispatch({ type: 'ADD_CHAT_MESSAGE', payload: { role: 'assistant', text: 'Sorry, I encountered an error.' } });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const sendAssistantMessage = async (message) => {
    // Send a message directly as the assistant (for welcome messages)
    dispatch({ type: 'ADD_CHAT_MESSAGE', payload: { role: 'assistant', text: message } });
  };

  // Send a welcome message once per step and reset chat only on the first send for that step
  const sendStepWelcome = async (step, message) => {
    // If we've already sent a welcome for this step, do nothing
    if (state.welcomesSent[step]) return;

    // Reset chat just once per step to give a clean slate for that agent
    dispatch({ type: 'SET_CHAT_HISTORY', payload: [] });

    // In import mode, fetch the LLM-generated welcome from backend
    if (state.importMode && state.importSessionId && state.course) {
      dispatch({ type: 'SET_LOADING', payload: true });
      try {
        const { sendExecutionMessage } = await import('@/services/importApi');
        // Send initial greeting trigger
        const data = await sendExecutionMessage(state.importSessionId, "ξεκινάμε");

        // Add the LLM-generated welcome (no user message shown)
        if (data.response) {
          dispatch({ type: 'ADD_CHAT_MESSAGE', payload: { role: 'assistant', text: data.response } });
        }

        if (data.course_data) {
          dispatch({ type: 'SET_COURSE', payload: data.course_data });
        }
      } catch (error) {
        console.error('Error getting import welcome:', error);
        dispatch({ type: 'ADD_CHAT_MESSAGE', payload: { role: 'assistant', text: 'Γεια σας! Ας ξεκινήσουμε με τις βελτιώσεις.' } });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    } else {
      // Regular mode - use provided message
      dispatch({ type: 'ADD_CHAT_MESSAGE', payload: { role: 'assistant', text: message } });
    }

    dispatch({ type: 'MARK_WELCOME_SENT', payload: step });
  };

  const generateGoals = async () => {
    if (!state.course) return;
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      // Use chat API to generate goals through conversation
      const data = await chatAPI.sendMessage(state.course.id, "Please generate 6 learning goals based on Bloom's Taxonomy for this course", 2);
      // The agent will handle goal generation through its tools
      console.log('Goals generation response:', data);

      // Update course data if returned from the chat response
      if (data.course_data) {
        dispatch({ type: 'SET_COURSE', payload: data.course_data });
      }
    } catch (error) {
      console.error('Error generating goals:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const generateSingleGoal = async (bloomLevel) => {
    if (!state.course) return null;
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      // Ask Coby explicitly to generate a single goal for the requested level
      const message = `Please generate a single new learning goal for Bloom level: ${bloomLevel}. Only add that one new goal.`;
      const data = await chatAPI.sendMessage(state.course.id, message, 2);
      if (data.course_data) {
        // Keep a convenience field if present
        const lastGenerated = data.course_data.lastGeneratedGoal || null;
        dispatch({ type: 'SET_COURSE', payload: data.course_data });
        return lastGenerated;
      }
      return null;
    } catch (error) {
      console.error('Error generating single goal:', error);
      return null;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };


  const generateActivities = async (onStepUpdate) => {
    if (!state.course) return;
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      // Single optimized call: Generate activities, topics, AND resources
      if (onStepUpdate) onStepUpdate('Generating activities, topics, and resources...');
      const data = await chatAPI.step3Part1(state.course.id);

      if (data.course_data) {
        dispatch({ type: 'SET_COURSE', payload: data.course_data });
      }

      if (onStepUpdate) onStepUpdate('Complete!');
    } catch (error) {
      console.error('Error generating activities:', error);
      if (onStepUpdate) onStepUpdate('Error occurred');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const generateInteractiveActivity = async (message) => {
    if (!state.course) return;
    try {
      // Use the interactive activity API
      const data = await chatAPI.interactiveActivity(state.course.id, message, state.currentStep);
      console.log('Interactive activity response:', data);

      // Update course data if returned from the response
      if (data.course_data) {
        dispatch({ type: 'SET_COURSE', payload: data.course_data });
      }

      return data;
    } catch (error) {
      console.error('Error with interactive activity:', error);
      return null;
    }
  };

  const applyTransform = async (action) => {
    if (!state.course) return;
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      // Use chat API to apply transforms through conversation
      const data = await chatAPI.sendMessage(state.course.id, `Please apply the following transformation: ${action}`, 4);
      // The agent will handle transformation through its tools
      console.log('Transform response:', data);
      return data;
    } catch (error) {
      console.error('Error applying transform:', error);
      return null;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const resetChatHistory = () => {
    dispatch({ type: 'SET_CHAT_HISTORY', payload: [] });
  };

  const setImportMode = (sessionId, executionPlan) => {
    dispatch({
      type: 'SET_IMPORT_MODE',
      payload: { sessionId, executionPlan }
    });
  };

  const clearImportMode = () => {
    dispatch({ type: 'CLEAR_IMPORT_MODE' });
  };

  const resetCourse = async (options = {}) => {
    // Clear all localStorage
    localStorage.removeItem(STORAGE_KEYS.COURSE_ID);
    localStorage.removeItem(STORAGE_KEYS.IMPORT_COURSE_ID);
    localStorage.removeItem(STORAGE_KEYS.IMPORT_SESSION_ID);

    // Clear URL params
    window.history.replaceState({}, document.title, window.location.pathname);

    // Reset state completely
    dispatch({ type: 'SET_COURSE', payload: null });
    dispatch({ type: 'SET_CHAT_HISTORY', payload: [] });
    dispatch({ type: 'SET_STEP', payload: 1 });
    dispatch({ type: 'CLEAR_IMPORT_MODE' });
    dispatch({ type: 'SET_ERROR', payload: null });
    dispatch({ type: 'SET_LOADING', payload: false });

    // Create fresh course with optional language settings
    try {
      const course = await courseAPI.create({
        language: options.language || "",
        interactionLanguage: options.interactionLanguage || "",
      });
      dispatch({ type: 'SET_COURSE', payload: course });
      localStorage.setItem(STORAGE_KEYS.COURSE_ID, course.id);
    } catch (error) {
      console.error('Error creating new course:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to create new course' });
    }
  };

  const value = {
    ...state,
    dispatch,
    updateCourse,
    createCheckpoint,
    restoreCheckpoint,
    sendChatMessage,
    sendAssistantMessage,
    sendStepWelcome,
    generateGoals,
    generateActivities,
    generateSingleGoal,
    generateInteractiveActivity,
    applyTransform,
    resetChatHistory,
    setImportMode,
    clearImportMode,
    resetCourse
  };

  return <CourseContext.Provider value={value}>{children}</CourseContext.Provider>;
}

export function useCourse() {
  const context = useContext(CourseContext);
  if (!context) {
    throw new Error('useCourse must be used within CourseProvider');
  }
  return context;
}